import { ABP, ListService, PagedResultDto, TrackByService } from '@abp/ng.core';
import { Confirmation, ConfirmationService } from '@abp/ng.theme.shared';
import { DateAdapter } from '@abp/ng.theme.shared/extensions';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbDateAdapter } from '@ng-bootstrap/ng-bootstrap';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import type {
  GetLegalDocumentsInput,
  LegalDocumentDto,
} from '../../../proxy/legal-documents/models';
import { LegalDocumentService } from '../../../proxy/legal-documents/legal-document.service';

@Component({
  selector: 'lib-legal-document',
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [ListService, { provide: NgbDateAdapter, useClass: DateAdapter }],
  templateUrl: './legal-document.component.html',
  styles: [],
})
export class LegalDocumentComponent implements OnInit {
  data: PagedResultDto<LegalDocumentDto> = {
    items: [],
    totalCount: 0,
  };

  filters = {} as GetLegalDocumentsInput;

  form: FormGroup;

  isFiltersHidden = true;

  isModalBusy = false;

  isModalOpen = false;

  selected?: LegalDocumentDto;

  constructor(
    public readonly list: ListService,
    public readonly track: TrackByService,
    public readonly service: LegalDocumentService,
    private confirmation: ConfirmationService,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    const getData = (query: ABP.PageQueryParams) =>
      this.service.getList({
        ...query,
        ...this.filters,
        filterText: query.filter,
      });

    const setData = (list: PagedResultDto<LegalDocumentDto>) => (this.data = list);

    this.list.hookToQuery(getData).subscribe(setData);
  }

  clearFilters() {
    this.filters = {} as GetLegalDocumentsInput;
  }

  buildForm() {
    const {
      description,
      expireDate,
    } = this.selected || {};

    this.form = this.fb.group({
      description: [description ?? null, [Validators.required]],
      expireDate: [expireDate ? new Date(expireDate) : null, []],
    });
  }

  hideForm() {
    this.isModalOpen = false;
    this.form.reset();
  }

  showForm() {
    this.buildForm();
    this.isModalOpen = true;
  }

  submitForm() {
    if (this.form.invalid) return;

    const request = this.selected
      ? this.service.update(this.selected.id, this.form.value)
      : this.service.create(this.form.value);

    this.isModalBusy = true;

    request
      .pipe(
        finalize(() => (this.isModalBusy = false)),
        tap(() => this.hideForm()),
      )
      .subscribe(this.list.get);
  }

  create() {
    this.selected = undefined;
    this.showForm();
  }

  update(record: LegalDocumentDto) {
    this.selected = record;
    this.showForm();
  }

  delete(record: LegalDocumentDto) {
    this.confirmation.warn(
      'Inventory::DeleteConfirmationMessage',
      'Inventory::AreYouSure',
      { messageLocalizationParams: [] }
    ).pipe(
      filter(status => status === Confirmation.Status.confirm),
      switchMap(() => this.service.delete(record.id)),
    ).subscribe(this.list.get);
  }
}
